import { Injectable } from '@angular/core';
import {
  CARD_TYPE,
  CreateMerchantRequest,
  CreateMerchantRequestInterface,
  FundingType,
  GetMerchantRequest,
  GetMerchantResponseInterface,
  MerchantApiService,
  MerchantInterface,
  PaymentCard,
  PaymentCardService,
  PaymentCardType,
  UpdateMerchantRequest,
  UpdateMerchantRequestInterface,
} from '@galaxy/billing';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { BehaviorSubject, Observable, ReplaySubject } from 'rxjs';
import { map, share, shareReplay } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class BillingSettingsService {
  constructor(
    private merchantApiService: MerchantApiService,
    private paymentCardService: PaymentCardService,
    private snackbarService: SnackbarService,
  ) {}

  public get merchantId(): string {
    return this.merchant;
  }

  public set merchantId(merchantId: string) {
    this.merchant = merchantId;
  }

  public get merchantDetailsInfo(): Observable<MerchantDetails> {
    return this.merchantDetails.asObservable();
  }

  public get failed(): Observable<boolean> {
    return this.hasFailed.asObservable();
  }

  public get isMerchantConfigured(): Observable<boolean> {
    return this.merchantConfigured.asObservable();
  }

  public get isPaymentDetailsConfigured(): Observable<boolean> {
    return this.paymentDetailsConfigured$.asObservable();
  }

  public get paymentDetailsInfo(): Observable<PaymentCard> {
    return this.paymentDetails.asObservable();
  }

  public get includeInFinancialRecords$(): Observable<boolean> {
    return this.merchantDetailsInfo.pipe(
      map((details) => (details && !!details.includeInFinancialRecords ? details.includeInFinancialRecords : false)),
      shareReplay(1),
    );
  }

  get loading(): Observable<boolean> {
    return this.isLoading.asObservable().pipe(share());
  }

  get submitting(): Observable<boolean> {
    return this.isSubmitting.asObservable().pipe(share());
  }

  get CreditCardInfoLoading(): Observable<boolean> {
    return this.isCreditCardInfoLoading$.asObservable();
  }

  get loadingValue(): boolean {
    return this.isLoading.getValue();
  }

  get submittingValue(): boolean {
    return this.isSubmitting.getValue();
  }

  get CreditCardInfoLoadingValue(): boolean {
    return this.isCreditCardInfoLoading$.getValue();
  }
  merchant = '';
  emptyPaymentDetails: PaymentCard = {
    cardId: '',
    fundingType: { value: '', id: FundingType.CREDIT },
    cardType: { value: '', id: PaymentCardType.VISA },
    lastFourDigits: '',
    expiryMonth: 0,
    expiryYear: 0,
  };

  merchantDetails: BehaviorSubject<MerchantDetails> = new BehaviorSubject<MerchantDetails>({ merchantId: '' });
  paymentDetails: BehaviorSubject<PaymentCard> = new BehaviorSubject<PaymentCard>(this.emptyPaymentDetails);

  private hasFailed: BehaviorSubject<boolean> = new BehaviorSubject(false);

  private isLoading: BehaviorSubject<boolean> = new BehaviorSubject(true);
  private isCreditCardInfoLoading$: BehaviorSubject<boolean> = new BehaviorSubject(true);

  private isSubmitting: BehaviorSubject<boolean> = new BehaviorSubject(false);

  private merchantConfigured: ReplaySubject<boolean> = new ReplaySubject();
  private paymentDetailsConfigured$: ReplaySubject<boolean> = new ReplaySubject();
  private paymentDetailsConfigured = false;

  static convertApiResponseToMerchantDetails(apiResponse: GetMerchantResponseInterface): MerchantDetails {
    const merchant: MerchantInterface = apiResponse.merchant;
    return {
      merchantId: merchant.merchantId,
      companyName: merchant.companyName,
      contactName: merchant.contactName,
      address: merchant.address,
      city: merchant.city,
      state: merchant.state,
      country: merchant.country,
      zipCode: merchant.zipCode,
      phoneNumber: merchant.phoneNumber,
      emailAddress: merchant.emailAddress,
      additionalEmailAddresses: merchant.additionalEmailAddresses,
      includeInFinancialRecords: merchant.includeInFinancialRecords,
    };
  }

  public updateMerchantDetails(details: MerchantDetails): void {
    this.merchantDetails.next(details);
    this.saveMerchantDetails();
  }

  public submitCreditCard(token: string, callback: (boolean) => unknown): void {
    let requestObservable;
    if (this.paymentDetailsConfigured) {
      requestObservable = this.paymentCardService.update(this.merchantId, token);
    } else {
      requestObservable = this.paymentCardService.create(this.merchantId, token);
    }

    requestObservable.subscribe(
      () => {
        this.loadMerchantCreditCards();
        this.loadMerchantDetails();
        this.snackbarService.successSnack('Payment method updated successfully!');
      },
      (resp) => {
        this.snackbarService.errorSnack(resp.error.message);
        callback(false);
      },
      () => callback(true),
    );
  }

  loadMerchantCreditCards(showErrorMessage = true): void {
    this.isCreditCardInfoLoading$.next(true);

    this.paymentCardService.getAll(this.merchantId).subscribe({
      next: (c) => {
        if (!(c && c.length)) {
          this.paymentDetailsConfigured$.next(false);
        } else {
          this.paymentDetails.next(c[0]);
          this.paymentDetailsConfigured$.next(true);
        }
        this.isCreditCardInfoLoading$.next(false);
      },
      error: (err) => {
        this.isCreditCardInfoLoading$.next(false);
        this.paymentDetailsConfigured$.next(false);
        if (err.status !== 404 && showErrorMessage) {
          this.snackbarService.errorSnack('Error getting payment details');
        }
      },
    });
    this.paymentDetailsConfigured$.subscribe((isConfigured) => {
      this.paymentDetailsConfigured = isConfigured;
    });
  }

  private convertCardTypeToString(cardType: CARD_TYPE): string {
    switch (cardType) {
      case CARD_TYPE.AMEX:
        return 'American Express';
      case CARD_TYPE.VISA:
        return 'Visa';
      case CARD_TYPE.MASTERCARD:
        return 'Mastercard';
      case CARD_TYPE.JCB:
        return 'JCB';
      case CARD_TYPE.DINERS_CLUB:
        return 'Diners Club';
      case CARD_TYPE.DISCOVER:
        return 'Discover';
      default:
        return 'Unknown';
    }
  }

  saveMerchantDetails(): void {
    this.isSubmitting.next(true);
    const merchantDetails: MerchantDetails = this.merchantDetails.value;
    const body: UpdateMerchantRequestInterface = {
      merchantId: this.merchant,
      contactName: merchantDetails.contactName,
      companyName: merchantDetails.companyName,
      address: merchantDetails.address,
      city: merchantDetails.city,
      state: merchantDetails.state,
      country: merchantDetails.country,
      zipCode: merchantDetails.zipCode,
      phoneNumber: merchantDetails.phoneNumber,
      emailAddress: merchantDetails.emailAddress,
      additionalEmailAddresses: merchantDetails.additionalEmailAddresses,
    };

    this.merchantApiService.update(new UpdateMerchantRequest(body)).subscribe(
      () => this._handleMerchantDetailsResponse(false, true, null, 'Successfully saved contact details'),
      (error) =>
        error.status === 404
          ? this.createNewMerchantDetails(body as CreateMerchantRequestInterface)
          : this._handleMerchantDetailsResponse(true, false, null, 'Error updating contact details'),
    );
  }

  createNewMerchantDetails(body: CreateMerchantRequestInterface): void {
    this.merchantApiService.create(new CreateMerchantRequest(body)).subscribe(
      () => this._handleMerchantDetailsResponse(false, true, null, 'Successfully created contact details'),
      () => this._handleMerchantDetailsResponse(true, false, null, 'Error creating contact details'),
    );
  }

  private _handleMerchantDetailsResponse(
    hasFailed: boolean,
    isMerchantConfigured: boolean,
    details?: MerchantDetails,
    message?: string,
  ): void {
    if (details) {
      this.merchantDetails.next(details);
    }

    if (message) {
      hasFailed ? this.snackbarService.errorSnack(message) : this.snackbarService.successSnack(message);
    }

    this.hasFailed.next(hasFailed);
    this.merchantConfigured.next(isMerchantConfigured);
    this.isLoading.next(false);
    this.isSubmitting.next(false);
  }

  public resetCache(): void {
    this.merchant = '';
    this.paymentDetailsConfigured = false;
    this.merchantDetails.next({ merchantId: '' });
    this.merchantConfigured.next(false);
    this.paymentDetailsConfigured$.next(false);
    this.paymentDetails.next(this.emptyPaymentDetails);
    this.hasFailed.next(false);
    this.isLoading.next(true);
    this.isCreditCardInfoLoading$.next(true);
    this.isSubmitting.next(false);
  }

  loadMerchantDetails(): void {
    this.isLoading.next(true);
    this.hasFailed.next(false);
    const request = new GetMerchantRequest({ merchantId: this.merchant });
    this.merchantApiService.get(request).subscribe(
      (response: GetMerchantResponseInterface) => {
        const details = BillingSettingsService.convertApiResponseToMerchantDetails(response);
        this._handleMerchantDetailsResponse(false, true, details);
      },
      (error) =>
        error.status === 404
          ? this._handleMerchantDetailsResponse(false, false, null)
          : this._handleMerchantDetailsResponse(true, false, {} as MerchantDetails),
    );
  }
}

export interface MerchantDetails {
  merchantId: string;
  companyName?: string;
  contactName?: string;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  zipCode?: string;
  phoneNumber?: string;
  emailAddress?: string;
  additionalEmailAddresses?: string[];
  includeInFinancialRecords?: boolean;
}

export interface StripeCardData {
  name: string;
  address_line1: string;
  address_city: string;
  address_state: string;
  address_country: string;
  address_zip: string;
}
